import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppShell } from '../components/Layout';
import { patientAPI, statsAPI } from '../services/api';
import PlanBadge from '../components/PlanBadge';
import Icon from '../components/Icon';
import OverflowMenu from '../components/OverflowMenu';
import avatarTint from '../utils/avatarTint';
import { displayPatientCode } from '../utils/patientCode';

const BREADCRUMBS = [{ label: 'Patients' }];
const PAGE_SIZE = 10;

const STATUS_COLORS = {
  ACTIVE:   'badge-active',
  PENDING:  'badge-pending',
  INACTIVE: 'badge-inactive',
};

// Phone layout (max-width 768px) — see the "Navigator Patients / Appointments
// lists — phone layout" block in index.css. Everything tagged mlist-* is
// either restyled there or shown on only one side of the breakpoint
// (mlist-phone-only / mlist-desktop-only); the desktop table is untouched.
const sentenceCase = (s = '') => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase().replace(/_/g, ' ');
const initials = (p) => `${p.firstName?.[0] || ''}${p.lastName?.[0] || ''}`.toUpperCase() || '?';

export default function PatientListPage() {
  const navigate = useNavigate();
  const [patients, setPatients]   = useState([]);
  const [stats, setStats]         = useState({});
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  // Dashboard cards deep-link here: ?status=ACTIVE, ?joined=week|month.
  const [params] = useSearchParams();
  const [statusFilter, setStatus] = useState(
    ['ACTIVE', 'PENDING', 'INACTIVE'].includes(params.get('status')) ? params.get('status') : 'ALL');
  const [joined, setJoined]       = useState(['week', 'month'].includes(params.get('joined')) ? params.get('joined') : null);
  const [page, setPage]           = useState(1);
  const [totalPages, setTotal]    = useState(1);
  const [totalCount, setCount]    = useState(0);
  const [exporting, setExporting] = useState(false);

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await patientAPI.list({
        page: page - 1,
        size: PAGE_SIZE,
        ...(search && { search }),
        ...(statusFilter !== 'ALL' && { status: statusFilter }),
        ...(joined && { joined }),
      });
      // Support both paginated { content, totalPages, totalElements } and plain array
      if (Array.isArray(res.data)) {
        setPatients(res.data);
        setTotal(1);
        setCount(res.data.length);
      } else {
        setPatients(res.data.content || res.data.patients || []);
        setTotal(res.data.totalPages || 1);
        setCount(res.data.totalElements || 0);
      }
    } catch {
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, joined]);

  useEffect(() => {
    const t = setTimeout(fetchPatients, search ? 400 : 0);
    return () => clearTimeout(t);
  }, [fetchPatients, search]);

  useEffect(() => {
    statsAPI.dashboard()
      .then(r => setStats(r.data))
      .catch(() => patientAPI.stats().then(r => setStats({ totalActive: r.data?.totalActive })).catch(() => {}));
  }, []);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await patientAPI.export({ search, status: statusFilter });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `patients-export-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // silent — matches fetchPatients' existing error handling on this page
    } finally {
      setExporting(false);
    }
  };

  // Pagination buttons are divs (kept, so desktop renders as before); these
  // give them a role, a tab stop and Enter/Space like a real button.
  const pageBtnProps = (label, enabled, go) => ({
    role: 'button',
    'aria-label': label,
    'aria-disabled': !enabled || undefined,
    tabIndex: enabled ? 0 : -1,
    onKeyDown: (e) => {
      if (enabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); go(); }
    },
  });

  return (
    <AppShell breadcrumbs={BREADCRUMBS}>
      <div className="page-header mlist-header fade-in-up">
        <div className="mlist-header-text">
          <div className="page-title">Patients</div>
          <div className="page-subtitle mlist-desktop-only">Manage and monitor all registered patients</div>
          {(!loading || totalCount > 0) && (
            <div className="mlist-count mlist-phone-only">{totalCount} registered</div>
          )}
        </div>
        <div className="page-actions">
          <button className="btn btn-ghost mlist-desktop-only" onClick={handleExport} disabled={exporting}>
            {exporting ? 'Exporting…' : '⬇ Export'}
          </button>
          <button className="btn btn-secondary mlist-desktop-only" onClick={() => navigate('/patients/import')}>
            <Icon name="importInto" size={15} /> Import Patients
          </button>
          <OverflowMenu
            className="mlist-phone-only"
            label="More patient actions"
            items={[
              { label: exporting ? 'Exporting…' : 'Export', onSelect: handleExport, disabled: exporting },
              { label: 'Import Patients', onSelect: () => navigate('/patients/import') },
            ]}
          />
          <button className="btn btn-primary mlist-primary" onClick={() => navigate('/patients/new')}>
            <span className="mlist-desktop-only">+ Add Patient</span>
            <span className="mlist-phone-only mlist-btn-label"><Icon name="plus" size={16} /> Add patient</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      {/* One bordered strip on phones (mlist-stats), four cards on desktop. */}
      <div className="stats-row mlist-stats fade-in-up delay-1">
        <div className="stat-card">
          <div className="stat-label">Total Patients</div>
          <div className="stat-value">{stats.totalPatients ?? totalCount ?? '—'}</div>
          <div className="stat-change">↑ registered</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Active</div>
          <div className="stat-value">
            {stats.totalActive ?? '—'}
          </div>
          <div className="stat-change">currently enrolled</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">New This Month</div>
          <div className="stat-value">{stats.newThisMonth ?? '—'}</div>
          <div className="stat-change">since 1st of month</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Appointments Today</div>
          <div className="stat-value">{stats.appointmentsToday ?? '—'}</div>
          <div className="stat-change" style={{ color: 'var(--amber)' }}>
            {stats.appointmentsPending ? `${stats.appointmentsPending} pending` : ''}
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="toolbar mlist-toolbar fade-in-up delay-2">
        <div className="search-input-wrap">
          <label className="mlist-sr-only" htmlFor="patient-search">Search patients</label>
          <span className="search-icon"><Icon name="search" size={15} /></span>
          <input
            id="patient-search"
            className="search-input"
            placeholder="Search by name, ID, phone…"
            value={search}
            onChange={handleSearchChange}
          />
        </div>
        {/* display: contents on desktop, so the chips stay toolbar items
            there; on phones this is their own horizontally scrolling line.
            Status and join date are filtered by the API. */}
        <div className="mlist-chips" role="group" aria-label="Filter by status">
          {['ALL', 'ACTIVE', 'PENDING', 'INACTIVE'].map(s => (
            <button
              key={s}
              className={`btn btn-sm mlist-chip ${statusFilter === s ? 'btn-primary' : 'btn-ghost'}`}
              aria-pressed={statusFilter === s}
              onClick={() => { setStatus(s); setPage(1); }}
            >
              {s === 'ALL' ? 'All Status' : s.charAt(0) + s.slice(1).toLowerCase()}
            </button>
          ))}
          {joined && (
            <button className="btn btn-sm mlist-chip btn-primary" aria-label="Clear joined filter"
              onClick={() => { setJoined(null); setPage(1); }}>
              {joined === 'week' ? 'New this week' : 'New this month'} ✕
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      {/* patients-table scopes the mobile column-hiding rules in index.css's
          "Patient list table mobile" block to just this table, instead of
          leaking into every other page's .table-wrap table (it used to hide
          the Users table's Actions column on mobile). */}
      <div className="table-wrap patients-table mlist-rows fade-in-up delay-3">
        <table>
          <thead>
            {/* 9 headers for 9 cells. "Patient ID" was missing, which shifted
                every header one column left of the data it labelled — the
                misalignment QA reported on this table. Keep this count and the
                colSpan values below in step with the cells. */}
            <tr>
              <th>Patient</th>
              <th>Patient ID</th>
              <th>Date of Birth</th>
              <th>Contact</th>
              <th>Navigator</th>
              <th>Last Visit</th>
              <th>Plan</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9}>
                  <div className="loading-state">
                    <div className="spinner" />
                    Loading patients…
                  </div>
                </td>
              </tr>
            ) : patients.length === 0 ? (
              <tr>
                <td colSpan={9}>
                  <div className="empty-state">
                    <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="people" size={20} /></div>
                    <div className="empty-title">No patients found</div>
                    <div className="empty-desc">
                      {search ? 'Try adjusting your search' : 'Add your first patient to get started'}
                    </div>
                  </div>
                </td>
              </tr>
            ) : patients.map((p) => (
              <tr
                key={p.id}
                className="row-link mlist-row"
                data-testid="patient-row-link"
                tabIndex={0}
                onClick={() => navigate(`/patients/${p.id}`)}
                onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) navigate(`/patients/${p.id}`); }}
              >
                {/* cell-stack keeps the diagnosis line under the name. The
                    mobile .table-wrap rules turn each td into a space-between
                    flex row, which otherwise sat the two side by side and let a
                    long diagnosis overlap the name. */}
                <td className="cell-stack mlist-c-main">
                  <span className={`avatar mlist-avatar mlist-phone-only ${avatarTint(`${p.firstName}${p.lastName}`, 4)}`} aria-hidden="true">
                    {initials(p)}
                  </span>
                  <div className="mlist-name">{p.firstName} {p.lastName}</div>
                  <div className="cell-sub mlist-sub">
                    <span className="mlist-desktop-only">{p.gender} · {p.medicalHistory || 'No diagnosis'}</span>
                    <span className="mlist-phone-only">
                      {[p.gender && sentenceCase(p.gender), p.medicalHistory || 'No diagnosis'].filter(Boolean).join(' · ')}
                    </span>
                  </div>
                </td>
                <td><span className="mono">{displayPatientCode(p.patientCode) || '—'}</span></td>
                <td>{p.dateOfBirth ? new Date(p.dateOfBirth).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
                <td>{p.phone || '—'}</td>
                <td>{p.navigatorName || p.navigator?.name || '—'}</td>
                <td>{p.lastVisit ? new Date(p.lastVisit).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</td>
                <td><PlanBadge patient={p} /></td>
                {/* Phones show the status chip only when it is not ACTIVE. */}
                <td className={`mlist-c-status${p.status === 'ACTIVE' ? ' mlist-is-active' : ''}`}>
                  <span className={`badge ${STATUS_COLORS[p.status] || 'badge-pending'}`}>
                    <span className="mlist-desktop-only">{p.status || 'Pending'}</span>
                    <span className="mlist-phone-only">{sentenceCase(p.status || 'Pending')}</span>
                  </span>
                </td>
                <td className="mlist-c-end">
                  <button
                    className="btn btn-secondary btn-sm mlist-desktop-only"
                    onClick={() => navigate(`/patients/${p.id}`)}
                  >
                    View →
                  </button>
                  <span className="mlist-chevron mlist-phone-only" aria-hidden="true"><Icon name="chevronRight" size={18} /></span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="table-footer mlist-footer">
          <span>
            {loading
              ? 'Loading…'
              : `Showing ${patients.length ? (page - 1) * PAGE_SIZE + 1 : 0}–${Math.min(page * PAGE_SIZE, totalCount)} of ${totalCount} patients`
            }
          </span>
          <div className="pagination">
            <div className={`page-btn ${page === 1 ? 'disabled' : ''}`}
              {...pageBtnProps('Previous page', page > 1, () => setPage(p => p - 1))}
              onClick={() => page > 1 && setPage(p => p - 1)}
              style={{ cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.4 : 1 }}
            >‹</div>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map(n => (
              <div
                key={n}
                className={`page-btn mlist-page-num ${page === n ? 'active' : ''}`}
                {...pageBtnProps(`Page ${n}`, true, () => setPage(n))}
                aria-current={page === n ? 'page' : undefined}
                onClick={() => setPage(n)}
              >{n}</div>
            ))}
            {totalPages > 5 && <div className="page-btn mlist-page-gap" style={{ cursor: 'default' }} aria-hidden="true">…</div>}
            <div
              className="page-btn"
              {...pageBtnProps('Next page', page < totalPages, () => setPage(p => p + 1))}
              onClick={() => page < totalPages && setPage(p => p + 1)}
              style={{ cursor: page === totalPages ? 'default' : 'pointer', opacity: page === totalPages ? 0.4 : 1 }}
            >›</div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
